import { G } from '@mobily/ts-belt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { required, SelectInput, useGetOne, useTranslate } from 'react-admin';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { EMPLOYEE_SAVINGS_MIN_NON_SELL_ALL_ARBITRAGE_ORDER_AMOUNT_CENTS as MIN_AMOUNT_CENTS } from '@Common/constants/employee_savings';
import {
  B2bPlanTypeErTaigaValues,
  b2bCompanyPlanTypeToCategory,
  isFullyManagedPlan,
} from '@Common/types/b2b/plans';
import { GetB2bEmployeesAvailablePositionsResponse } from '@Common/types/bo/b2b/employees/available_positions';
import {
  B2bEmployeeTaigaPlan,
  GetB2bEmployeesTaigaPlansIdEndpoint,
} from '@Common/types/bo/b2b/employees/taiga_plans';

import { Button, ShowSection } from '@sh4res/bo-common/components';
import {
  ArbitrageRow,
  emptyArbitrageRow,
  sumArbitrageRows,
} from '@sh4res/bo-common/components/B2bArbitrage/buildArbitrageBody';
import { InstrumentSelectInput } from '@sh4res/bo-common/components/B2bVoluntaryPayment/components/InstrumentSelectInput';
import {
  ExtraPlanChoice,
  PlanSelectChoice,
  PlanSelectInput,
} from '@sh4res/bo-common/components/B2bVoluntaryPayment/components/PlanSelectInput';
import { AmountInput } from '@sh4res/bo-common/fields';
import { formatCentsNumber, minValueCents } from '@sh4res/bo-common/utils';

type Side = 'sources' | 'targets';
type Holding = { id: number; name: string; amount: number };

// Taiga plans are not company plans: they come from the employee's own Taiga plans
const isTaigaPlan = (plan: PlanSelectChoice) =>
  (B2bPlanTypeErTaigaValues as readonly string[]).includes(plan.type);

const getPlanId = (row: ArbitrageRow) => row.plan_data?.id ?? row.taiga_plan?.id ?? null;

// Same bounds as the server: below the minimum only a full sale or a single purchase
const validateSourceAmount = (heldAmount: number | null) => (value: number | null) => {
  if (G.isNullable(value) || G.isNullable(heldAmount)) {
    return undefined;
  }
  if (value > heldAmount) {
    return {
      message: 'resources.bo_common.b2b.arbitrage_create.validation.exceeds_position',
      args: { max: formatCentsNumber(heldAmount) },
    };
  }
  return value < MIN_AMOUNT_CENTS && value !== heldAmount
    ? {
        message: 'resources.bo_common.b2b.arbitrage_create.validation.below_minimum',
        args: { min: formatCentsNumber(MIN_AMOUNT_CENTS) },
      }
    : undefined;
};

const validateTargetAmount = (hasSeveralTargets: boolean) => (value: number | null) =>
  hasSeveralTargets && G.isNotNullable(value) && value < MIN_AMOUNT_CENTS
    ? {
        message: 'resources.bo_common.b2b.arbitrage_create.validation.below_minimum',
        args: { min: formatCentsNumber(MIN_AMOUNT_CENTS) },
      }
    : undefined;

const useEmployeeTaigaPlans = (employeeId: number | null): B2bEmployeeTaigaPlan[] => {
  const { data } = useGetOne<GetB2bEmployeesTaigaPlansIdEndpoint['Client']['ResponseBody']>(
    'b2b/employees/taiga_plans',
    { id: employeeId ?? 0 },
    { enabled: G.isNotNullable(employeeId) },
  );

  return data?.plans ?? [];
};

const FundsRows = ({ side }: { side: Side }) => {
  const testId =
    side === 'sources'
      ? 'BoExternalB2bArbitrageCreateSourcesStep'
      : 'BoExternalB2bArbitrageCreateTargetsStep';
  const { control, setValue } = useFormContext();
  const translate = useTranslate();

  const [companyId, employeeId, contractIds]: [number | null, number | null, number[] | null] =
    useWatch({ name: ['b2b_company_id', 'b2b_employee_id', 'contract_ids'] });
  const rows: ArbitrageRow[] = useWatch({ name: side }) ?? [];
  const otherRows: ArbitrageRow[] =
    useWatch({ name: side === 'sources' ? 'targets' : 'sources' }) ?? [];
  const taigaPlans = useEmployeeTaigaPlans(employeeId);

  const extraChoices: ExtraPlanChoice[] = useMemo(
    () => taigaPlans.map((plan) => ({ id: plan.id, name: plan.label, disabled: plan.read_only })),
    [taigaPlans],
  );

  const { fields, append, remove } = useFieldArray({ control, name: side });

  useEffect(() => {
    if (fields.length === 0) {
      append(emptyArbitrageRow(), { shouldFocus: false });
    }
  }, [fields.length, append]);

  const resetRow = (index: number) => {
    setValue(`${side}.${index}.instrument_id`, null);
    setValue(`${side}.${index}.amount`, null);
  };

  const handlePlanChange = (index: number, plan: PlanSelectChoice | undefined) => {
    if (G.isNullable(plan)) {
      return;
    }
    setValue(`${side}.${index}.plan_data`, plan);
    setValue(`${side}.${index}.taiga_plan`, null);
    resetRow(index);
  };

  const handleTaigaPlanChange = (index: number, choice: ExtraPlanChoice | undefined) => {
    if (G.isNullable(choice)) {
      return;
    }
    setValue(`${side}.${index}.plan_data`, null);
    setValue(
      `${side}.${index}.taiga_plan`,
      taigaPlans.find((plan) => plan.id === choice.id),
    );
    resetRow(index);
  };

  return (
    <ShowSection
      leftAligned
      label={`resources.bo_common.b2b.arbitrage_create.sections.${side}`}
      noCard={true}>
      <Typography variant="subtitle1">
        {translate(
          `resources.bo_common.b2b.arbitrage_create.fields.${side === 'sources' ? 'total_sold' : 'total_bought'}`,
        )}
        : {formatCentsNumber(sumArbitrageRows(rows))}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map((field, index) => {
          // Watched rows can lag one render behind the field array
          const row = rows.at(index);
          const planData = row?.plan_data ?? null;
          const taigaPlan = row?.taiga_plan ?? null;
          const hasPlan = G.isNotNullable(planData) || G.isNotNullable(taigaPlan);
          const isManaged = planData ? isFullyManagedPlan(planData.type) : false;
          // At most one managed plan per arbitrage
          const otherRowsOfArbitrage = [
            ...rows.filter((_row, rowIndex) => rowIndex !== index),
            ...otherRows,
          ];
          const hasOtherManagedPlan = otherRowsOfArbitrage.some(
            (other) => other.plan_data && isFullyManagedPlan(other.plan_data.type),
          );
          // Taiga and internal plans can't be mixed
          const hasOtherTaigaPlan = otherRowsOfArbitrage.some((other) => other.taiga_plan);
          const hasOtherInternalPlan = otherRowsOfArbitrage.some((other) => other.plan_data);
          // One tax wrapper per arbitrage
          const otherCategories = otherRowsOfArbitrage.flatMap((other) =>
            other.plan_data ? [b2bCompanyPlanTypeToCategory(other.plan_data.type)] : [],
          );
          // A fund is picked once per side and not sold and bought on the same plan
          const planId = getPlanId(row ?? emptyArbitrageRow());
          const takenInstrumentIds = new Set([
            ...rows
              .filter((_row, rowIndex) => rowIndex !== index)
              .map((other) => other.instrument_id),
            ...otherRows
              .filter((other) => getPlanId(other) === planId)
              .map((other) => other.instrument_id),
          ]);

          return (
            <Box key={field.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: '300px' }}>
                <PlanSelectInput
                  data-testid={`${testId}_PlanSelectInput`}
                  source={`${side}.${index}.saving_plan_id`}
                  label="resources.bo_common.b2b.arbitrage_create.fields.saving_plan_id"
                  filter={{
                    company_id: companyId ?? undefined,
                    b2b_contract_id: contractIds ?? [],
                  }}
                  requireVoluntaryDeposit={false}
                  disablePlan={(plan) =>
                    isTaigaPlan(plan) ||
                    hasOtherTaigaPlan ||
                    (hasOtherManagedPlan && isFullyManagedPlan(plan.type)) ||
                    otherCategories.some(
                      (category) => category !== b2bCompanyPlanTypeToCategory(plan.type),
                    )
                  }
                  extraChoices={extraChoices.map((choice) => ({
                    ...choice,
                    disabled: choice.disabled || hasOtherInternalPlan,
                  }))}
                  validate={[required()]}
                  onPlanChange={(plan) => handlePlanChange(index, plan)}
                  onExtraPlanChange={(choice) => handleTaigaPlanChange(index, choice)}
                  sx={{ width: '100%', marginBottom: 0 }}
                />
              </Box>
              {side === 'sources' ? (
                <SourceFund
                  testId={testId}
                  index={index}
                  employeeId={employeeId}
                  planData={planData}
                  taigaPlan={taigaPlan}
                  isManaged={isManaged}
                  amount={row?.amount ?? null}
                  takenInstrumentIds={takenInstrumentIds}
                />
              ) : (
                <>
                  <Box sx={{ width: '300px', display: 'flex', flexDirection: 'column' }}>
                    {isManaged && <ManagedPlanLabel />}
                    {hasPlan && !isManaged && (
                      <InstrumentSelectInput
                        data-testid={`${testId}_InstrumentSelectInput`}
                        source={`${side}.${index}.instrument_id`}
                        instrumentIds={(
                          planData?.instruments_universe ??
                          taigaPlan?.instrument_ids ??
                          []
                        ).filter((instrumentId) => !takenInstrumentIds.has(instrumentId))}
                        isCashSourcesPerInstrument={false}
                        instrumentsCashSourcesLibre={planData?.instruments_cash_sources_libre ?? []}
                        label="resources.bo_common.b2b.arbitrage_create.fields.instrument"
                        validate={[required()]}
                        sx={{ width: '100%', marginBottom: 0 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ width: '150px' }}>
                    {hasPlan && (
                      <AmountInput
                        testId={`${testId}_AmountInput`}
                        source={`${side}.${index}.amount`}
                        label="resources.bo_common.b2b.arbitrage_create.fields.amount"
                        validate={[
                          required(),
                          minValueCents(1),
                          validateTargetAmount(rows.length > 1),
                        ]}
                        sx={{ width: '100%', marginBottom: 0 }}
                      />
                    )}
                  </Box>
                </>
              )}
              <IconButton
                data-testid={`${testId}_RemoveButton`}
                onClick={() => remove(index)}
                sx={{ mt: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}
        <Box>
          <IconButton
            data-testid={`${testId}_AddButton`}
            onClick={() => append(emptyArbitrageRow(), { shouldFocus: false })}>
            <AddIcon />
            <Typography variant="body2" sx={{ ml: 1 }}>
              {translate('resources.bo_common.b2b.arbitrage_create.actions.add_allocation')}
            </Typography>
          </IconButton>
        </Box>
      </Box>
    </ShowSection>
  );
};

const ManagedPlanLabel = () => {
  const translate = useTranslate();

  return (
    <Typography sx={{ textAlign: 'center', paddingTop: '10px' }} color="text.secondary">
      {translate('resources.bo_common.b2b.arbitrage_create.messages.managed_plan')}
    </Typography>
  );
};

// Internal plans sell from the employee's available positions: the amounts the arbitrage
// validation accepts, c3 excluded and partially restricted funds capped to their withdrawable part
const useHoldings = ({
  employeeId,
  planData,
  taigaPlan,
}: {
  employeeId: number | null;
  planData: PlanSelectChoice | null;
  taigaPlan: B2bEmployeeTaigaPlan | null;
}): Holding[] => {
  const isEnabled = G.isNotNullable(planData) && G.isNotNullable(employeeId);
  const { data: availablePositions } = useGetOne<GetB2bEmployeesAvailablePositionsResponse>(
    'b2b/employees/available_positions',
    { id: employeeId ?? 0, meta: { query: { saving_plan_id: String(planData?.id) } } },
    { enabled: isEnabled },
  );

  return useMemo(() => {
    if (taigaPlan) {
      return taigaPlan.positions.map((position) => ({
        id: position.instrument_id,
        name: position.name,
        amount: Math.round(position.amount),
      }));
    }

    return (availablePositions?.allocations ?? []).map((allocation) => ({
      id: allocation.instrument_id,
      name: allocation.name,
      amount: allocation.amount,
    }));
  }, [taigaPlan, availablePositions]);
};

const SourceFund = ({
  testId,
  index,
  employeeId,
  planData,
  taigaPlan,
  isManaged,
  amount,
  takenInstrumentIds,
}: {
  testId: string;
  index: number;
  employeeId: number | null;
  planData: PlanSelectChoice | null;
  taigaPlan: B2bEmployeeTaigaPlan | null;
  isManaged: boolean;
  amount: number | null;
  takenInstrumentIds: Set<number | null>;
}) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const instrumentId: number | null = useWatch({ name: `sources.${index}.instrument_id` });

  const holdings = useHoldings({ employeeId, planData, taigaPlan });

  const choices = useMemo(
    () =>
      holdings
        .filter((holding) => !takenInstrumentIds.has(holding.id))
        .map((holding) => ({
          id: holding.id,
          name: `${holding.name} (${formatCentsNumber(holding.amount)})`,
        })),
    [holdings, takenInstrumentIds],
  );

  // A managed plan is sold as a whole: its position is the plan total
  const heldAmount = isManaged
    ? holdings.reduce((total, holding) => total + holding.amount, 0)
    : (holdings.find((holding) => holding.id === instrumentId)?.amount ?? null);

  if (G.isNullable(planData) && G.isNullable(taigaPlan)) {
    return null;
  }

  return (
    <>
      <Box sx={{ width: '300px', display: 'flex', flexDirection: 'column' }}>
        {isManaged ? (
          <ManagedPlanLabel />
        ) : (
          <SelectInput
            data-testid={`${testId}_InstrumentSelectInput`}
            source={`sources.${index}.instrument_id`}
            label="resources.bo_common.b2b.arbitrage_create.fields.instrument"
            choices={choices}
            helperText={
              choices.length === 0
                ? translate('resources.bo_common.b2b.arbitrage_create.messages.no_holdings')
                : undefined
            }
            validate={[required()]}
            sx={{ width: '100%', marginBottom: 0 }}
          />
        )}
      </Box>
      <Box sx={{ width: '220px', display: 'flex', flexDirection: 'column' }}>
        <AmountInput
          testId={`${testId}_AmountInput`}
          source={`sources.${index}.amount`}
          label="resources.bo_common.b2b.arbitrage_create.fields.amount"
          validate={[required(), minValueCents(1), validateSourceAmount(heldAmount)]}
          sx={{ width: '100%', marginBottom: 0 }}
        />
        {G.isNotNullable(heldAmount) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {translate('resources.bo_common.b2b.arbitrage_create.fields.held', {
                amount: formatCentsNumber(heldAmount),
              })}
            </Typography>
            <Button
              data-testid={`${testId}_FullPositionButton`}
              label="resources.bo_common.b2b.arbitrage_create.actions.full_position"
              permissions={{ resource: 'b2b/individual_operations', action: 'create' }}
              size="small"
              disabled={amount === heldAmount}
              onClick={() =>
                setValue(`sources.${index}.amount`, heldAmount, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export const FundsStep = () => (
  <>
    <FundsRows side="sources" />
    <FundsRows side="targets" />
  </>
);
